import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { themes, DEFAULT_THEME, THEME_STORAGE_KEY } from "../config/themes";

// Create context
const ThemeContext = createContext();

/**
 * Themes are stored per portal ("scope"), so an owner and a tenant sharing the
 * same browser each keep their own colour choice instead of overwriting one
 * another. The wrapper of each portal declares its scope on mount:
 *
 *   TenantWrapper -> "tenant"   (ots_theme_tenant)
 *   OwnerWrapper  -> "owner"    (ots_theme_owner)
 *   anything else -> "default"  (ots_theme — the original key)
 */
const scopedKey = (scope) =>
  scope && scope !== "default" ? `${THEME_STORAGE_KEY}_${scope}` : THEME_STORAGE_KEY;

// A scope that has never been picked falls back to the pre-scope key, so nobody
// loses the theme they chose before this existed.
const readTheme = (scope) => {
  const saved = localStorage.getItem(scopedKey(scope));
  if (saved && themes[saved]) return saved;

  const legacy = localStorage.getItem(THEME_STORAGE_KEY);
  return legacy && themes[legacy] ? legacy : DEFAULT_THEME;
};

/**
 * Theme Provider Component
 * Wrap your app with this to enable global theming
 */
export const ThemeProvider = ({ children }) => {
  const [scope, setScope] = useState("default");
  const [currentTheme, setCurrentTheme] = useState(() => readTheme("default"));

  /* Called by a portal wrapper to claim its own saved theme. */
  const setThemeScope = useCallback((next) => {
    setScope((prev) => (prev === (next || "default") ? prev : next || "default"));
  }, []);

  /* Entering a portal loads that portal's saved theme. Persistence happens in
     changeTheme only — writing from here would push the previous scope's colour
     into the new scope's key before the read lands. */
  useEffect(() => {
    setCurrentTheme(readTheme(scope));
  }, [scope]);

  // Change theme function
  const changeTheme = useCallback(
    (themeName) => {
      if (!themes[themeName]) return;
      setCurrentTheme(themeName);
      localStorage.setItem(scopedKey(scope), themeName);
    },
    [scope]
  );

  // Get current theme object
  const theme = themes[currentTheme] || themes[DEFAULT_THEME];

  const value = useMemo(
    () => ({
      currentTheme,
      theme,
      themes,
      changeTheme,
      scope,
      setThemeScope,
    }),
    [currentTheme, theme, changeTheme, scope, setThemeScope]
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
};

/**
 * Custom hook to use theme
 * @returns {object} { currentTheme, theme, themes, changeTheme, scope, setThemeScope }
 */
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};

export default ThemeContext;
